/**
 * Запис власного профілю: `PATCH /api/v1/me` з `If-Match` за версією з `GET /me` (user-cabinet.md,
 * розд. 10.5). Ім'я, мова й пояс змінюються тим самим маршрутом (мову й пояс людина тепер змінює
 * тут, а не через `/me/preferences`, #330).
 *
 * `412` — профіль змінили деінде (ім'я змінює й адміністратор): перечитуємо `/me` і повторюємо ту
 * саму зміну один раз поверх свіжої версії. Решта помилок — до викликача.
 */
import type { MePatch, MePatchResponse, MeResponse } from '@clientline/contracts';
import type { QueryClient } from '@tanstack/react-query';
import { ApiProblem, api, fetchMe, platformKeys } from './api.ts';
import { queryPolicy } from './query-policy.ts';

const send = (patch: MePatch, version: number) =>
  api<MePatchResponse>('PATCH', '/api/v1/me', { body: patch, ifMatch: version });

/** Зберегти зміну й оновити кеш `/me`. Повертає нову версію. */
export async function saveMe(
  queryClient: QueryClient,
  me: Pick<MeResponse, 'version'>,
  patch: MePatch,
): Promise<number> {
  let result: MePatchResponse;
  try {
    result = await send(patch, me.version);
  } catch (error) {
    if (!(error instanceof ApiProblem && error.status === 412)) throw error;
    const fresh = await queryClient.fetchQuery({
      queryKey: platformKeys.me,
      queryFn: ({ signal }) => fetchMe(signal),
      ...queryPolicy.fresh,
    });
    result = await send(patch, fresh.version);
  }
  queryClient.setQueryData<MeResponse>(platformKeys.me, (old) =>
    old ? { ...old, ...definedOnly(patch), version: result.version } : old,
  );
  return result.version;
}

function definedOnly(patch: MePatch): Partial<MeResponse> {
  const out: Partial<MeResponse> = {};
  if (patch.displayName !== undefined) out.displayName = patch.displayName;
  if (patch.locale !== undefined) out.locale = patch.locale;
  if (patch.timeZone !== undefined) out.timeZone = patch.timeZone;
  return out;
}
